import React from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Generic destructive-action confirmation modal. Renders only when `open`.
 * Replace `window.confirm` calls with this component.
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *   ...
 *   setConfirm({
 *     title: "Remove member?",
 *     message: "They will lose access immediately.",
 *     confirmLabel: "Remove",
 *     onConfirm: async () => doRemove(),
 *   });
 *   <Confirm state={confirm} onClose={() => setConfirm(null)} />
 */
export default function Confirm({ state, onClose }) {
  const [busy, setBusy] = React.useState(false);
  if (!state) return null;
  const {
    title = "Are you sure?",
    message = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = true,
    onConfirm,
  } = state;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="confirm-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className={`bg-card border ${
          destructive ? "border-destructive/40" : "border-border"
        } rounded-md w-full max-w-md p-6 relative`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${
            destructive ? "bg-destructive/15" : "bg-primary/15"
          }`}>
            <AlertTriangle className={`w-5 h-5 ${destructive ? "text-destructive" : "text-primary"}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg tracking-tight">{title}</h3>
            {message && (
              <p className="text-sm text-muted-foreground mt-1.5">{message}</p>
            )}
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            data-testid="confirm-dialog-confirm"
            type="submit"
            disabled={busy}
            className={`px-4 py-2 text-sm rounded-md transition disabled:opacity-50 ${
              destructive
                ? "bg-destructive text-destructive-foreground hover:opacity-90"
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
